<template>
  <div class="pt-12 md:pt-10">
    <div class="px-3 md:container">
      <div class="lg:flex">
        <div class="left-flex hidden lg:block">
          <span class="ota-body-light" v-text="$t('FAQ.BackToOverview')" />
          <div
            v-for="topic in topics"
            :key="`${topic.url}-${topic.title}`"
            class="mt-3 px-6"
          >
            <NuxtLink
              class="ota-body-light"
              :to="{ path: $i18n.localePath(topic.to) }"
            >
              {{ topic.title }}
            </NuxtLink>
          </div>
        </div>
        <div class="right-flex">
          <NuxtLink
            class="inline-flex items-center focus:outline-none ota-body-light"
            :to="{ path: $i18n.localePath({ name: 'customer-service' }) }"
          >
            <OSvg svg="arrow-left" class="w-3 fill-gray-600 pt-1" />
            <span v-text="$t('FAQ.BackToOverview')" />
          </NuxtLink>
          <h1 v-if="currentTopic" class="mt-3 mb-9 ota-h2 font-semibold" v-text="currentTopic.title" />
          <template v-if="currentTopic">
            <Accordion
              v-for="(topic, i) in currentTopic.questions"
              :key="i"
              :ref="`topic-${i}`"
              :year="topic.question"
              :open="$route.query.question === topic.path"
              class="mb-3"
            >
              <template v-slot:header>
                <div class="ota-p14 font-semibold text-left" v-html="topic.question" />
              </template>

              <template>
                <span class="accordion-content" v-html="topic.answer" />
                <span class="flex accordion-satisfaction">
                  <span class="block pb-3 pr-rem" v-text="$t('FAQ.DidThisHelpYou')" />
                  <OSvg
                    class="w-5 h-5 mr-rem"
                    :class="{'thumb-up-active': getFeedback(topic.path) === 'positive' }"
                    svg="thumb-up"
                    @click.native="clickFeedback({ positive: true, path: topic.path })"
                  />
                  <OSvg
                    class="w-5 h-5"
                    :class="{'thumb-down-active': getFeedback(topic.path) === 'negative'}"
                    svg="thumb-down"
                    @click.native="clickFeedback({ positive: false, path: topic.path })"
                  />
                </span>
                <transition appear name="fade">
                  <div v-if="getFeedback(topic.path) === 'positive'" class="pb-3">
                    <span v-text="$t('FAQ.ThankyouForYouFeedback')" />
                  </div>
                </transition>
                <transition appear name="fade">
                  <div v-if="getFeedback(topic.path) === 'negative'" class="pb-3">
                    <span v-text="$t('FAQ.ThankyouForYouFeedbackNegative')" />
                    <div class="text-left pt-3">
                      <OButton class="text-white" tag="a" :to="chatbotLink" variant="secondary">
                        <span v-text="$t('CustomerService.ContactBlock.Chat')" />
                      </OButton>
                    </div>
                  </div>
                </transition>
              </template>
            </Accordion>
          </template>
        </div>
      </div>
    </div>
    <ContactBlock :locale="locale" class="my-20" />
  </div>
</template>

<script>
import customerServiceMixin from '@/mixins/customerService'
import OSvg from '@/components/OSvg'
import Accordion from '@/components/Accordion'
import ContactBlock from '@/components/ContactBlock'
import OButton from '@/components/OButton'
import { mapState, mapGetters } from 'vuex'
import { pushToDataLayer } from '@/utils/gtm'
import { AUTH_ID } from '@/utils/store/getterTypes'
import { stripLanguageRegex } from '@/utils/general'

export default {
  name: 'CustomerServiceTopic',
  layout: 'customer-service',
  components: { OSvg, ContactBlock, Accordion, OButton },
  mixins: [customerServiceMixin],
  data () {
    return {
      feedback: {}
    }
  },
  computed: {
    currentTopic () {
      const pathWithoutLang = `${this.$route.path.replace(stripLanguageRegex, '')}`
      const url = pathWithoutLang.replace(/\//g, '-').replace('-', '')

      return this.topics.find(topic => topic.url === url)
    },
    chatbotLink () {
      return `${process.env.CHATBOT_BASE_URL}/${this.locale}`
    },
    ...mapState('app', {
      locale: state => state.locale
    }),
    ...mapGetters('auth', {
      userId: AUTH_ID
    })
  },
  methods: {
    clickFeedback ({ positive = true, path }) {
      this.feedback = {
        ...this.feedback,
        [path]: positive
      }

      pushToDataLayer({
        event: `faq_${path}`,
        event_action: positive ? 'thump_up' : 'thump_down',
        userId: `${this.userId ?? ''}`
      })
    },
    getFeedback (path) {
      if (this.feedback[path] === undefined) {
        return ''
      }

      return this.feedback[path] ? 'positive' : 'negative'
    }
  }
}
</script>

<style lang="postcss" scoped>
.fade-enter {
  @apply opacity-0;
}

.fade-enter-active {
  @apply transition-opacity duration-500 ease-in;
}

.left-flex {
  >>> .nuxt-link-exact-active,
  >>> .nuxt-link-active {
    @apply relative font-semibold;

    &::before {
      @apply absolute bottom-0 h-full bg-blue-400 w-1;
      left: -1.2rem;
      top: 0;
      bottom: 0;
      margin: auto;
      content: "";
    }
  }
}

.bg-blue-400 {
  background-color: #6362EC;
}

.border-b:last-child {
  @apply border-none;
}

.accordion {
  >>> .accordion-body {
    @apply p-0;

    button {
      @apply rounded p-3;

      svg {
        @apply w-3;
      }
    }
  }

  >>> .accordion-content {
    @apply px-3;
  }

  >>> .accordion-satisfaction {
    color: #A3A3A3;

    svg {
      @apply cursor-pointer;

      path {
        @apply transition-all duration-200 ease-in;
        fill: #262626 !important;
        stroke: #262626 !important;
      }

      &.thumb-up-active {
        path {
          fill: #00A351 !important;
          stroke: #00A351 !important;
        }
      }

      &.thumb-down-active {
        path {
          fill: #E6464C !important;
          stroke: #E6464C !important;
        }
      }
    }
  }

  >>> .backdrop {
    @apply p-0;

    & > div {
      @apply rounded;
    }
  }
}

h1 {
  font-size: 24px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

>>> .accordion-content {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;

  a {
    @apply text-orange-400 underline;

    &.obutton {
      @apply text-white no-underline;
    }
  }

  p {
    @apply pb-5;
  }

  ol {
    @apply list-decimal list-inside;

    p {
      @apply inline;
    }

    li {
      @apply pb-1;
    }
  }

  ul {
    @apply list-disc list-inside;

    p {
      @apply inline;
    }

    li {
      @apply pb-1;
    }
  }
}

@screen lg {
  .left-flex {
    flex: 30;
    max-width: 30%;
  }

  .right-flex {
    flex: 70;
  }
}
</style>
